import { useCallback, useEffect, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { adjustUnreadCount, bumpChat } from "../../../app/reducers/book/notifies"
import { extractMainMessage } from "../../utils/serializers/chat"
import { READ_MESSAGE_STATUS, UNREAD_MESSAGE_STATUS } from "../../utils/variables/chat"

export const formatTime = () => {
  const d = new Date()
  const pad = (n) => String(n).padStart(2, "0")
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} | ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`
}

const useChatMessages = ({
  departments,
  getChatDetail,
  createNotify,
  locale,
  currentCompanyId,
  currentUserId,
  sortByLastMessage,
}) => {
  const dispatch = useDispatch()

  const getChatDetailRef = useRef(getChatDetail)
  useEffect(() => {
    getChatDetailRef.current = getChatDetail
  }, [getChatDetail])

  const createNotifyRef = useRef(createNotify)
  useEffect(() => {
    createNotifyRef.current = createNotify
  }, [createNotify])

  const departmentsRef = useRef(departments)
  useEffect(() => {
    departmentsRef.current = departments
  }, [departments])

  const localeRef = useRef(locale)
  useEffect(() => {
    localeRef.current = locale
  }, [locale])

  const currentCompanyIdRef = useRef(currentCompanyId)
  useEffect(() => {
    currentCompanyIdRef.current = currentCompanyId
  }, [currentCompanyId])

  const [chatMessages, setChatMessages] = useState({})
  const [loadingChats, setLoadingChats] = useState({})
  const [errorChats, setErrorChats] = useState({})
  // Метаданные чата из chat_detail (например, привязка лида к боту Telegram/Messenger).
  const [chatMeta, setChatMeta] = useState({})

  const buildDepartmentMap = () => {
    const map = new Map()
    departmentsRef.current?.forEach((d) => {
      if (d?.id != null) map.set(d.id, d.name ?? "")
    })
    return map
  }

  const mapNotify = useCallback((msg, { departmentMap, curCompanyId, bundleId }) => {
    const isAiBotMsg = msg.role != null

    return {
      id: msg.id,
      sender: isAiBotMsg ? (msg.role === "bot" ? "AI Bot" : msg.author_name ?? "") : (msg.user_name ?? msg.name ?? ""),
      company: msg.company_name ?? "",
      text: extractMainMessage(isAiBotMsg ? msg.content : msg.message),
      time: (() => {
        if (!msg.created_at) return ""
        const d = new Date(msg.created_at)
        if (isNaN(d.getTime())) return ""
        const pad = (n) => String(n).padStart(2, "0")
        return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} | ${pad(d.getHours())}:${pad(d.getMinutes())}`
      })(),
      status: isAiBotMsg
        ? READ_MESSAGE_STATUS
        : msg.is_read
          ? READ_MESSAGE_STATUS
          : UNREAD_MESSAGE_STATUS,
      isOwn: isAiBotMsg ? msg.role === "bot" : msg.user_company_id === curCompanyId,
      isEditing: false,
      isHidden: msg.is_hidden ?? false,
      files:
        Array.isArray(msg.files) && msg.files.length > 0
          ? msg.files.map((f) => ({
              url: f.url ?? "",
              file_name: f.file_name ?? "",
            }))
          : [],
      bundleId: msg.bundle_id ?? msg.lead_id ?? (isAiBotMsg ? bundleId : null),
      department: departmentMap.get(msg.departament_id) ?? "",
      department_id: msg.departament_id ?? null,
      company_id: msg.user_company_id ?? null,
      avatar: msg.avatar ?? null,
      for_admin: msg.for_admin ?? false,
      to_client: msg.to_client ?? false,
      from_client: msg.from_client ?? false,
      read_by: msg?.read_log ?? [],
      reply_to_id: msg.reply_to_id ?? null,
      from_partner: msg?.provider_id > 0 || msg?.is_provider_notify === true,
      is_system:
        !isAiBotMsg &&
        !msg?.user_id &&
        !msg?.manager_id &&
        !(msg?.provider_id > 0) &&
        !msg?.is_provider_notify,
      lead_id: msg.lead_id ?? null,
      lead_name: msg.lead_name ?? null,
      lead_status_name: msg.lead_status_name ?? null,
      lead_status_color: msg.lead_status_color ?? null,
      lead_status_bg: msg.lead_status_bg ?? null,
      is_hidden: msg?.is_hidden ?? false,
      message_theme: msg?.message_theme ?? null,
    }
  }, [])

  const fetchMessages = useCallback(
    async (bundleId, isSendMsg = false, fetcherOverride = null) => {
      if (!bundleId) return

      if (!isSendMsg) {
        setLoadingChats((prev) => ({ ...prev, [bundleId]: true }))
        setChatMessages((prev) => ({ ...prev, [bundleId]: [] }))
      }
      setErrorChats((prev) => ({ ...prev, [bundleId]: null }))

      try {
        const fetcher = fetcherOverride ?? getChatDetailRef.current
        const response = await fetcher({ bundle_id: bundleId }).unwrap()

        const rawList = response.data ?? response.notifies ?? response.messages ?? []

        if (response && "client_linked" in response) {
          setChatMeta((prev) => ({
            ...prev,
            [bundleId]: {
              client_linked: Boolean(response.client_linked),
              client_channel: response.client_channel ?? null,
              client_reply_window: response.client_reply_window ?? null,
            },
          }))
        }

        const departmentMap = buildDepartmentMap()
        const curCompanyId = currentCompanyIdRef.current

        const mapped = (Array.isArray(rawList) ? rawList : []).map((msg) =>
          mapNotify(msg, { departmentMap, curCompanyId, bundleId })
        )

        if (isSendMsg) {
          setChatMessages((prev) => {
            const existing = prev[bundleId] ?? []
            const existingIds = new Set(existing.map((m) => m.id))
            const newMessages = mapped.filter((m) => !existingIds.has(m.id))
            if (newMessages.length === 0) return prev
            return { ...prev, [bundleId]: [...existing, ...newMessages] }
          })
        } else {
          setChatMessages((prev) => ({ ...prev, [bundleId]: mapped }))
        }
      } catch (err) {
        console.error("fetchMessages error", err)
        setErrorChats((prev) => ({ ...prev, [bundleId]: err }))
      } finally {
        if (!isSendMsg) {
          setLoadingChats((prev) => ({ ...prev, [bundleId]: false }))
        }
      }
    },
    [mapNotify]
  )

  const receiveMessage = useCallback(
    (bundleId, rawMsg) => {
      if (!bundleId || !rawMsg) return

      const departmentMap = buildDepartmentMap()
      const curCompanyId = currentCompanyIdRef.current
      const mapped = mapNotify(rawMsg, { departmentMap, curCompanyId, bundleId })

      setChatMessages((prev) => {
        const existing = prev[bundleId] ?? []
        if (existing.some((m) => m.id === mapped.id)) return prev
        return { ...prev, [bundleId]: [...existing, mapped] }
      })
    },
    [mapNotify]
  )

  const applyReadStatus = useCallback((bundleId, notifyId, isRead, readLogHistory) => {
    setChatMessages((prev) => ({
      ...prev,
      [bundleId]: (prev[bundleId] ?? []).map((m) =>
        m.id === notifyId
          ? {
              ...m,
              status: isRead ? READ_MESSAGE_STATUS : UNREAD_MESSAGE_STATUS,
              read_by: readLogHistory ?? m.read_by,
            }
          : m
      ),
    }))
  }, [])

  // приход события 'department_change' через ActionCable (см. broadcast_department_change).
  // departmentName приходит с бэка, но пересчитываем и по локальной departmentMap на
  // случай рассинхрона со списком департаментов на клиенте.
  const applyDepartmentChange = useCallback((bundleId, notifyId, departmentId, departmentName) => {
    const departmentMap = buildDepartmentMap()
    const resolvedName = departmentMap.get(departmentId) ?? departmentName ?? ""

    setChatMessages((prev) => ({
      ...prev,
      [bundleId]: (prev[bundleId] ?? []).map((m) =>
        m.id === notifyId
          ? { ...m, department: resolvedName, department_id: departmentId ?? null }
          : m
      ),
    }))
  }, [])

  const getMessages = useCallback((bundleId) => chatMessages[bundleId] ?? [], [chatMessages])
  const getChatMeta = useCallback((bundleId) => chatMeta[bundleId] ?? null, [chatMeta])
  const isLoading = useCallback((bundleId) => loadingChats[bundleId] ?? false, [loadingChats])
  const getError = useCallback((bundleId) => errorChats[bundleId] ?? null, [errorChats])

  const sendMessage = useCallback(
    async (
      bundleId,
      {
        text,
        department,
        attachedFiles,
        theme,
        for_admin = false,
        to_client = false,
        chat_type = "bundle",
        reply_to_id = null,
        provider_id,
        remote_order,
      }
    ) => {
      if (!text.trim() && !attachedFiles?.length) return

      const payload = {
        bundle_id: bundleId,
        message: text.trim(),
        departament_id: department,
        provider_id: provider_id,
        for_admin: for_admin,
      }

      if (to_client) payload.to_client = true

      if (remote_order) payload.remote_order = remote_order
      if (reply_to_id) payload.reply_to_id = reply_to_id
      if (attachedFiles?.length) payload.files = attachedFiles
      if (theme) payload.type = theme

      try {
        const created = await createNotifyRef.current(payload).unwrap()

        if (sortByLastMessage) {
          // Чаты лидов в списке идентифицируются по lead_id, заявок — по bundle_id.
          dispatch(
            bumpChat({
              bundle_id: chat_type === "lead" ? undefined : bundleId,
              lead_id: chat_type === "lead" ? bundleId : undefined,
              message: text.trim(),
              created_at: new Date().toISOString(),
            })
          )
        }

        await fetchMessages(bundleId, true)
        return { clientDeliveryFailed: Boolean(created?.client_delivery_failed) }
      } catch (err) {
        console.error("sendMessage error", err)
        return null
      }
    },
    [fetchMessages, sortByLastMessage, dispatch]
  )

  const toggleEdit = useCallback((bundleId, msgId) => {
    setChatMessages((prev) => ({
      ...prev,
      [bundleId]: (prev[bundleId] ?? []).map((m) =>
        m.id === msgId ? { ...m, isEditing: true, editDraft: m.text } : m
      ),
    }))
  }, [])

  const saveEdit = useCallback(async (bundleId, msgId, newText, updateNotifyMessage) => {
    if (!newText.trim()) return

    try {
      await updateNotifyMessage({ id: msgId, message: newText.trim() }).unwrap()

      setChatMessages((prev) => ({
        ...prev,
        [bundleId]: (prev[bundleId] ?? []).map((m) =>
          m.id === msgId
            ? { ...m, text: newText.trim(), isEditing: false, edited: true, editDraft: undefined }
            : m
        ),
      }))
    } catch (err) {
      console.error("saveEdit error", err)
    }
  }, [])

  const cancelEdit = useCallback((bundleId, msgId) => {
    setChatMessages((prev) => ({
      ...prev,
      [bundleId]: (prev[bundleId] ?? []).map((m) =>
        m.id === msgId ? { ...m, isEditing: false, editDraft: undefined } : m
      ),
    }))
  }, [])

  // изменение департамента локально (используется при optimistic-апдейте после
  // собственного вызова updateDepartment) — остаётся как было
  const changeDept = useCallback((bundleId, msgId, newDeptId, newDeptName) => {
    setChatMessages((prev) => ({
      ...prev,
      [bundleId]: (prev[bundleId] ?? []).map((m) =>
        m.id === msgId ? { ...m, department: newDeptName, department_id: newDeptId } : m
      ),
    }))
  }, [])

  const isInDepartmentScope = (msg, departmentIds) =>
  !departmentIds || departmentIds.length === 0 || departmentIds.includes(String(msg.department_id))

  const toggleReadStatus = useCallback(
    async (bundleId, msgId, { toggleReadMsg, departmentIds = [] }) => {
      const messages = chatMessages[bundleId] ?? []
      const targetMsg = messages.find((m) => m.id === msgId)
      if (!targetMsg) return

      const affectsCounter = isInDepartmentScope(targetMsg, departmentIds)
      const isCurrentlyRead = targetMsg.status === READ_MESSAGE_STATUS
      const newStatus = isCurrentlyRead ? UNREAD_MESSAGE_STATUS : READ_MESSAGE_STATUS
      const delta = isCurrentlyRead ? 1 : -1

      const adjust = (d) => {
        if (!affectsCounter) return
        dispatch(
          adjustUnreadCount({
            bundle_id: targetMsg.lead_id ? undefined : bundleId,
            lead_id: targetMsg.lead_id ?? undefined,
            delta: d,
          })
        )
      }

      const setStatus = (status) =>
        setChatMessages((prev) => ({
          ...prev,
          [bundleId]: (prev[bundleId] ?? []).map((m) => (m.id === msgId ? { ...m, status } : m)),
        }))

      setStatus(newStatus)
      adjust(delta)

      try {
        const response = await toggleReadMsg(msgId).unwrap()

        if (response.read_log) {
          setChatMessages((prev) => ({
            ...prev,
            [bundleId]: (prev[bundleId] ?? []).map((m) =>
              m.id === msgId ? { ...m, read_by: response.read_log.history ?? [] } : m
            ),
          }))
        }

        const actualStatus = response.is_read ? READ_MESSAGE_STATUS : UNREAD_MESSAGE_STATUS
        if (actualStatus !== newStatus) {
          adjust(-delta)
          setStatus(actualStatus)
        }
      } catch (err) {
        console.error("toggleReadStatus error", err)
        adjust(-delta)
        setStatus(targetMsg.status)
      }
    },
    [chatMessages, dispatch]
  )

    const markAsRead = useCallback(
    async (bundleId, { toggleReadMsg, departmentIds = [] }) => {
      let unread = []

      setChatMessages((prev) => {
        const messages = prev[bundleId] ?? []
        unread = messages.filter(
          (m) =>
            m.status !== READ_MESSAGE_STATUS && !m.isOwn && isInDepartmentScope(m, departmentIds)
        )
        if (unread.length === 0) return prev

        const unreadIds = new Set(unread.map((u) => u.id))
        return {
          ...prev,
          [bundleId]: messages.map((m) =>
            unreadIds.has(m.id) ? { ...m, status: READ_MESSAGE_STATUS } : m
          ),
        }
      })

      await Promise.resolve()
      if (unread.length === 0) return

      dispatch(
        adjustUnreadCount({
          bundle_id: unread[0]?.lead_id ? undefined : bundleId,
          lead_id: unread[0]?.lead_id ?? undefined,
          delta: -unread.length,
        })
      )

      const results = await Promise.allSettled(unread.map((msg) => toggleReadMsg(msg.id).unwrap()))

      results.forEach((result, i) => {
        if (result.status !== "fulfilled" || !result.value) return
        const readLog = result.value.read_log
        if (!readLog) return

        const msgId = unread[i].id
        setChatMessages((prev) => ({
          ...prev,
          [bundleId]: (prev[bundleId] ?? []).map((m) =>
            m.id === msgId ? { ...m, read_by: readLog.history ?? [] } : m
          ),
        }))
      })
    },
    [dispatch]
  )

  return {
    getMessages,
    getChatMeta,
    fetchMessages,
    isLoading,
    getError,
    sendMessage,
    toggleEdit,
    saveEdit,
    cancelEdit,
    changeDept,
    toggleReadStatus,
    markAsRead,
    receiveMessage,
    applyReadStatus,
    applyDepartmentChange,
  }
}

export default useChatMessages
